import React, { useEffect, useMemo, useRef, useState } from 'react';
import socketService from './socketService';
import './styles/online.css';

const CHAT_MAX_LEN = 160;
const MAX_MESSAGES = 80;

const ChatWidget = ({ scope, roomId, currentUserId, title, quickMessages = [], dockedOnDesktop = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [unread, setUnread] = useState(0);
  const [statusMsg, setStatusMsg] = useState(null);
  const listRef = useRef(null);

  const events = useMemo(() => {
    if (scope === 'game') {
      return { send: 'chat:game-send', receive: 'chat:game-message' };
    }
    return { send: 'chat:lobby-send', receive: 'chat:lobby-message' };
  }, [scope]);

  useEffect(() => {
    if (scope === 'game' && !roomId) return undefined;

    const handleMessage = (data) => {
      if (!data || !data.text) return;
      setMessages(prev => {
        const next = [...prev, data];
        if (next.length > MAX_MESSAGES) next.shift();
        return next;
      });
      if (!isOpen) setUnread(prev => prev + 1);
    };

    const cleanup = socketService.on(events.receive, handleMessage);
    return () => cleanup && cleanup();
  }, [events.receive, isOpen, roomId, scope]);

  useEffect(() => {
    const handleRateLimited = (data) => {
      if (data?.event !== events.send) return;
      setStatusMsg('Demasiados mensajes, espera un momento');
      setTimeout(() => setStatusMsg(null), 2500);
    };

    const cleanup = socketService.on('rate-limited', handleRateLimited);
    return () => cleanup && cleanup();
  }, [events.send]);

  useEffect(() => {
    if (!isOpen) return;
    setUnread(0);
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [isOpen, messages]);

  const sendMessage = (text, quick = false) => {
    if (!text || typeof text !== 'string') return;
    const clean = text.trim().slice(0, CHAT_MAX_LEN);
    if (!clean) return;

    if (!socketService.isConnected) {
      setStatusMsg('Sin conexion, reintenta');
      setTimeout(() => setStatusMsg(null), 2500);
      return;
    }

    const payload = scope === 'game'
      ? { roomId, text: clean, quick }
      : { text: clean, quick };

    socketService.emit(events.send, payload);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    sendMessage(input, false);
    setInput('');
  };

  const handleQuick = (msg) => {
    sendMessage(msg, true);
  };

  const safeTitle = title || (scope === 'game' ? 'Chat partida' : 'Chat lobby');
  const shouldRenderPanel = isOpen;

  useEffect(() => {
    if (!dockedOnDesktop || typeof window === 'undefined') return undefined;
    const media = window.matchMedia('(min-width: 769px)');

    const syncOpen = () => {
      setIsOpen(media.matches);
    };

    syncOpen();
    if (media.addEventListener) {
      media.addEventListener('change', syncOpen);
      return () => media.removeEventListener('change', syncOpen);
    }
    media.addListener(syncOpen);
    return () => media.removeListener(syncOpen);
  }, [dockedOnDesktop]);

  return (
    <div className={`online-chat-widget ${scope} ${dockedOnDesktop ? 'docked' : ''} ${isOpen ? 'is-open' : ''}`}>
      <button className="online-chat-fab" onClick={() => setIsOpen(true)}>
        💬 {safeTitle}
        {unread > 0 && <span className="online-chat-unread">{unread}</span>}
      </button>

      {isOpen && (
        <button
          type="button"
          className="online-chat-backdrop"
          aria-label="Cerrar chat"
          onClick={() => setIsOpen(false)}
        />
      )}

      {shouldRenderPanel && (
        <div className="online-chat-panel">
          <div className="online-chat-header">
            <span>{safeTitle}</span>
            <button className="online-chat-close" onClick={() => setIsOpen(false)}>✕</button>
          </div>

          {statusMsg && (
            <div className="online-chat-status">{statusMsg}</div>
          )}

          <div className="online-chat-messages" ref={listRef}>
            {messages.length === 0 && (
              <div className="online-chat-empty">Sin mensajes</div>
            )}
            {messages.map((msg, idx) => {
              const isMe = msg.userId === currentUserId;
              const authorName = isMe ? 'Tú' : (msg.username || 'Jugador');
              return (
                <div key={msg.id || `${msg.userId}-${msg.ts}-${idx}`} className={`online-chat-message ${isMe ? 'me' : ''}`}>
                  <span className="online-chat-user">{authorName}</span>
                  <span className={`online-chat-bubble ${msg.quick ? 'quick' : ''}`}>{msg.text}</span>
                </div>
              );
            })}
          </div>

          {quickMessages.length > 0 && (
            <div className="online-chat-quick">
              {quickMessages.map((msg) => (
                <button key={msg} className="online-chat-quick-btn" onClick={() => handleQuick(msg)}>
                  {msg}
                </button>
              ))}
            </div>
          )}

          <form className="online-chat-input" onSubmit={handleSubmit}>
            <input
              type="text"
              maxLength={CHAT_MAX_LEN}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Escribe un mensaje..."
            />
            <button type="submit">Enviar</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
